// src/components/NewEnrichmentModal.tsx
import React, { useState, useCallback, useRef } from 'react';
import { X, Upload, FileText, FolderPlus } from 'lucide-react';
import { useAppContext } from '../hooks/useAppContext';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../hooks/useAuth';
import { API_BASE_URL } from '../constants';
import './NewEnrichmentModal.css';
import './list-selection-modal.css';

interface NewEnrichmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
}


const NewEnrichmentModal: React.FC<NewEnrichmentModalProps> = ({
  isOpen,
  onClose,
  onComplete
}) => {
  const { showToast, addJobId, state } = useAppContext();
  const { apiKey } = useAuth();
  const { makeRequest, createList, getLists, getSchema, getExportConfig } = useApi();
  
  
  const [uploadId, setUploadId] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState<'upload' | 'mapping' | 'results' | 'enriching' | 'matches_found' | 'list_selection' | 'unlocking' | 'unlock_complete' | 'exporting' | 'export_complete' | 'complete'>('upload');
  const [columnMapping, setColumnMapping] = useState<any>(null);
  const [userMapping, setUserMapping] = useState<{
    linkedin_url_column: string | null;
    email_column: string | null;
    phone_column: string | null;
  }>({
    linkedin_url_column: null,
    email_column: null,
    phone_column: null
  });
  const [uploadDetails, setUploadDetails] = useState<any>(null);
  const [enrichmentStatus, setEnrichmentStatus] = useState<any>(null);
  const [unlockJobId, setUnlockJobId] = useState<string | null>(null);
  const [unlockJobResult, setUnlockJobResult] = useState<any>(null);
  const [exportJobId, setExportJobId] = useState<string | null>(null);
  const [finalResults, setFinalResults] = useState<any>(null);
  const [searchResults, setSearchResults] = useState<any>(null);
  
  
  // Field selection state for export step
  const [selectedFields, setSelectedFields] = useState<Set<string>>(new Set());
  const [format, setFormat] = useState<'csv' | 'json'>('csv');
  const [fieldGroups, setFieldGroups] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [defaultFields, setDefaultFields] = useState<string[]>([]);
  const [loadingFields, setLoadingFields] = useState(false);
  
  // Contact list selection state
  const [contactLists, setContactLists] = useState<any[]>([]);
  const [selectedListIds, setSelectedListIds] = useState<Set<string>>(new Set());
  const [loadingLists, setLoadingLists] = useState(false);
  const [showCreateList, setShowCreateList] = useState(false);
  const [newListName, setNewListName] = useState('');
  
  // Load field schema when export step is reached
  const loadFieldSchema = useCallback(async () => {
    setLoadingFields(true);
    try {
      const [schemaData, exportConfigData] = await Promise.all([
        getSchema(),
        getExportConfig()
      ]);
      
      if (schemaData) {
        // Group fields by section
        const sections = schemaData.data_sources?.contacts?.sections || {};
        const groups: any[] = [];
        
        Object.entries(sections).forEach(([sectionName, fieldNames]) => {
          if (Array.isArray(fieldNames)) {
            const sectionFields = fieldNames
              .map(fieldName => {
                const definition = schemaData.field_definitions?.[fieldName];
                return definition ? { name: fieldName, definition } : null;
              })
              .filter((field): field is any => field !== null);
            
            if (sectionFields.length > 0) {
              groups.push({
                name: sectionName,
                fields: sectionFields
              });
            }
          }
        });
        
        setFieldGroups(groups);
      }
      
      if (exportConfigData?.export_fields) {
        setDefaultFields(exportConfigData.export_fields);
        // Set default selected fields
        setSelectedFields(new Set(exportConfigData.export_fields));
      }
    } catch (error) {
      console.error('Failed to load schema or export config:', error);
    } finally {
      setLoadingFields(false);
    }
  }, [getSchema, getExportConfig]);
  
  // Load contact lists
  const loadContactLists = useCallback(async () => {
    setLoadingLists(true);
    try {
      const data = await getLists(true) as { success: boolean; lists?: any[] };
      if (data?.success) {
        setContactLists(data.lists || []);
      } else {
        throw new Error('Failed to load contact lists');
      }
    } catch (error) {
      console.error('Failed to load contact lists:', error);
      setContactLists([]);
      showToast({
        type: 'error',
        title: 'Error Loading Lists',
        message: 'Failed to load contact lists'
      });
    } finally {
      setLoadingLists(false);
    }
  }, [getLists, showToast]);
  
  // Field selection handlers
  const handleFieldToggle = (fieldName: string) => {
    const newSelected = new Set(selectedFields);
    if (newSelected.has(fieldName)) {
      newSelected.delete(fieldName);
    } else {
      newSelected.add(fieldName);
    }
    setSelectedFields(newSelected);
  };

  const selectAllFields = () => {
    const allFields = fieldGroups.flatMap(group => 
      group.fields.map((field: any) => field.name)
    );
    setSelectedFields(new Set(allFields));
  };

  const selectDefaultFields = () => {
    setSelectedFields(new Set(defaultFields));
  };

  const clearAllFields = () => {
    setSelectedFields(new Set());
  };

  // Load field schema when export step is reached
  React.useEffect(() => {
    if (currentStep === 'exporting') {
      loadFieldSchema();
      setSearchTerm('');
    }
  }, [currentStep, loadFieldSchema]);
  
  // Form data
  const [enrichmentName, setEnrichmentName] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  // Loading states
  const [uploading, setUploading] = useState(false);
  const [loadingMapping, setLoadingMapping] = useState(false);
  const [startingEnrichment, setStartingEnrichment] = useState(false);
  const [monitoringEnrichment, setMonitoringEnrichment] = useState(false);
  const [creatingUnlock, setCreatingUnlock] = useState(false);
  const [monitoringUnlock, setMonitoringUnlock] = useState(false);
  const [creatingExport, setCreatingExport] = useState(false);
  const [monitoringExport, setMonitoringExport] = useState(false);
  
  const fileInputRef = useRef<HTMLInputElement>(null);


  // Reset form when modal opens
  React.useEffect(() => {
    if (isOpen) {
      setUploadId(null);
      setEnrichmentName('');
      setSelectedFile(null);
      setCurrentStep('upload');
      setColumnMapping(null);
      setUploadDetails(null);
      setEnrichmentStatus(null);
      setUnlockJobId(null);
      setExportJobId(null);
      setFinalResults(null);
      setContactLists([]);
      setSelectedListIds(new Set());
      setLoadingLists(false);
      setShowCreateList(false);
      setNewListName('');
    }
  }, [isOpen]);

  // Handle file selection
  const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      if (!enrichmentName) {
        setEnrichmentName(file.name.replace('.csv', ''));
      }
    }
  }, [enrichmentName]);

  // Handle file upload
  const handleUpload = useCallback(async () => {
    if (!selectedFile || !enrichmentName || !apiKey) {
      showToast({ type: 'error', title: 'Missing Info', message: 'Please provide an enrichment name and select a file.' });
      return;
    }
    
    setUploading(true);
    try {
      
      // First create a new list using the enrichment name
      const listResponse = await createList(enrichmentName, `List for enrichment: ${enrichmentName}`);
      
      if (!listResponse?.list_id) {
        throw new Error('Failed to create list');
      }

      // Then upload the file with the new list ID
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('enrichment_name', enrichmentName);
      formData.append('target_list_id', listResponse.list_id);
      const response = await makeRequest<{ upload_id?: string; error?: string }>(`${API_BASE_URL}/api/enrichment/source/csv/contacts/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${apiKey}`
          // Note: Don't set Content-Type for FormData - let the browser set it with boundary
        },
        body: formData
      });

      if (response?.data?.upload_id) {
        setUploadId(response.data.upload_id);
        // Store the target_list_id from the upload response
        if (listResponse?.list_id) {
          setUploadDetails(prev => ({
            ...prev,
            target_list_id: listResponse.list_id
          }));
        }
        showToast({
          type: 'success',
          title: 'File Uploaded',
          message: `CSV file uploaded successfully. New list "${enrichmentName}" created.`
        });
        // Move to mapping step instead of closing modal
        await loadUploadDetails(response.data.upload_id);
      } else {
        const errorMsg = response?.data?.error || 'Upload failed';
        console.error('Upload failed - response:', response);
        throw new Error(errorMsg);
      }
    } catch (error) {
      console.error('Upload failed:', error);
      let errorMessage = 'Failed to upload CSV file';
      
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      } else if (error && typeof error === 'object') {
        // Handle API validation errors
        if (Array.isArray(error)) {
          errorMessage = error.map(err => 
            typeof err === 'object' && err.msg ? err.msg : String(err)
          ).join(', ');
        } else if (error.detail) {
          errorMessage = error.detail;
        } else if (error.message) {
          errorMessage = error.message;
        } else {
          errorMessage = JSON.stringify(error);
        }
      }
      
      showToast({
        type: 'error',
        title: 'Upload Failed',
        message: errorMessage
      });
    } finally {
      setUploading(false);
    }
  }, [selectedFile, enrichmentName, apiKey, createList, makeRequest, showToast]);

  // Load upload details and column mapping
  const loadUploadDetails = useCallback(async (uploadId: string) => {
    setLoadingMapping(true);
    try {
      const response = await makeRequest<{
        upload_id: string;
        file_name: string;
        headers: string[];
        column_mapping: any;
        enrichment_status: string;
      }>(`${API_BASE_URL}/api/enrichment/source/csv/contacts/upload/${uploadId}`, {
        method: 'GET'
      });

      if (response?.data) {
        // Preserve the target_list_id from upload if it exists
        setUploadDetails(prev => ({
          ...response.data,
          target_list_id: prev?.target_list_id || response.data.target_list_id
        }));
        setColumnMapping(response.data.column_mapping);
        
        // Set user mapping based on high confidence matches only (>= 0.7)
        const mapping = response.data.column_mapping;
        const confidenceThreshold = 0.7;
        
        setUserMapping({
          linkedin_url_column: (mapping.confidence_scores?.linkedin_url >= confidenceThreshold) ? mapping.linkedin_url_column : null,
          email_column: (mapping.confidence_scores?.email >= confidenceThreshold) ? mapping.email_column : null,
          phone_column: (mapping.confidence_scores?.phone >= confidenceThreshold) ? mapping.phone_column : null
        });
        setCurrentStep('mapping');
      } else {
        throw new Error('Failed to load upload details');
      }
    } catch (error) {
      console.error('Failed to load upload details:', error);
      showToast({
        type: 'error',
        title: 'Error',
        message: 'Failed to load column mapping details'
      });
    } finally {
      setLoadingMapping(false);
    }
  }, [makeRequest, showToast]);

  // Start enrichment process and continue through all steps
  const handleMappingChange = useCallback((field: 'linkedin_url_column' | 'email_column' | 'phone_column', value: string) => {
    setUserMapping(prev => ({
      ...prev,
      [field]: value === '' ? null : value
    }));
  }, []);

  const startEnrichment = useCallback(async () => {
    if (!uploadId) return;
    
    setStartingEnrichment(true);
    try {
      
      const response = await makeRequest<{
        success: boolean;
        enrichment_id: string;
        message: string;
        target_list_id?: string;
      }>(`${API_BASE_URL}/api/enrichment/source/csv/contacts/search`, {
        method: 'POST',
        body: JSON.stringify({
          upload_id: uploadId,
          enrichment_name: enrichmentName,
          target_list_id: uploadDetails?.target_list_id,
          include_uuid_list: true,
          column_mapping: {
            linkedin_url_column: userMapping.linkedin_url_column || '',
            email_column: userMapping.email_column || '',
            phone_column: userMapping.phone_column || ''
          }
        })
      });

      if (response?.data?.success) {
        const searchResults = response.data;
        showToast({
          type: 'success',
          title: 'Search Completed',
          message: `Found ${searchResults.person_uuids?.length || 0} matches. Ready to proceed to export.`
        });
        setCurrentStep('results');
        // Store search results for export
        setSearchResults(searchResults);
      } else {
        throw new Error(response?.data?.message || 'Search failed');
      }
    } catch (error) {
      console.error('Failed to start enrichment:', error);
      showToast({
        type: 'error',
        title: 'Search Failed',
        message: 'Failed to search for matches'
      });
    } finally {
      setStartingEnrichment(false);
    }
  }, [uploadId, enrichmentName, uploadDetails, makeRequest, showToast]);

  // Monitor enrichment progress
  const monitorEnrichment = useCallback(async () => {
    if (!uploadId) return;
    
    setMonitoringEnrichment(true);
    const maxAttempts = 15;
    let attempt = 0;
    
    const checkStatus = async () => {
      try {
        const response = await makeRequest<{
          status: string;
          enrichment_name: string;
          target_list_id: string;
          stats: any;
          person_uuids: string[];
          estimated_cost: number;
        }>(`${API_BASE_URL}/api/enrichment/source/csv/contacts/status/${uploadId}`, {
          method: 'GET'
        });

        if (response?.data) {
          const status = response.data.status;
          // Preserve the target_list_id from uploadDetails if not in response
          setEnrichmentStatus(prev => ({
            ...response.data,
            target_list_id: response.data.target_list_id || prev?.target_list_id || uploadDetails?.target_list_id
          }));
          
          if (status === 'completed' || status === 'not_started') {
            showToast({
              type: 'success',
              title: 'Enrichment Complete',
              message: `Found ${response.data.person_uuids?.length || 0} contacts.`
            });
            setCurrentStep('matches_found');
            // Don't automatically start unlock - let user decide
          } else if (status === 'failed') {
            throw new Error('Enrichment failed');
          } else if (attempt < maxAttempts) {
            // Still processing, check again in 3 seconds
            setTimeout(checkStatus, 3000);
            attempt++;
          } else {
            throw new Error('Enrichment timed out');
          }
        }
      } catch (error) {
        console.error('Failed to monitor enrichment:', error);
        showToast({
          type: 'error',
          title: 'Error',
          message: 'Failed to monitor enrichment progress'
        });
      } finally {
        setMonitoringEnrichment(false);
      }
    };
    
    checkStatus();
  }, [uploadId, uploadDetails, makeRequest, showToast]);

  // Create unlock jobs
  const createUnlockJobs = useCallback(async (personUuids: string[], targetListIdOrIds?: string | string[], enrichmentData?: any) => {
    // Handle both single targetListId and array of list_ids
    let listIds: string[];
    if (Array.isArray(targetListIdOrIds)) {
      listIds = targetListIdOrIds;
    } else {
      const listId = targetListIdOrIds || enrichmentStatus?.target_list_id;
      listIds = listId ? [listId] : [];
    }
    
    if (!personUuids.length || !listIds.length) {
      console.error('Missing required data for unlock job:', { personUuids: personUuids.length, listIds });
      return;
    }
    
    
    setCreatingUnlock(true);
    try {
      // Use passed enrichment data if available, otherwise fall back to state
      const uploadIdToUse = enrichmentData?.upload_id || enrichmentStatus?.upload_id || uploadId;
      const enrichmentIdToUse = enrichmentData?.enrichment_id || enrichmentStatus?.enrichment_id;
      
      
      const response = await makeRequest<{
        job_id: string;
      }>(`${API_BASE_URL}/api/contacts/unlock-jobs`, {
        method: 'POST',
        body: JSON.stringify({
          person_uuids: personUuids,
          list_ids: listIds,
          enrichment_metadata: {
            upload_id: uploadIdToUse,
            enrichment_id: enrichmentIdToUse
          }
        })
      });


      if (response?.data?.job_id) {
        setUnlockJobId(response.data.job_id);
        showToast({
          type: 'success',
          title: 'Unlock Job Created',
          message: `Unlock job created for ${personUuids.length} contacts.`
        });
        // Add job to global polling system (same as search flow)
        addJobId(response.data.job_id, 'contacts_unlock');
      } else {
        console.error('No job_id in response:', response);
        console.error('Full response structure:', JSON.stringify(response, null, 2));
        throw new Error('Failed to create unlock job');
      }
    } catch (error) {
      console.error('Failed to create unlock job:', error);
      console.error('Error details:', {
        message: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
        name: error instanceof Error ? error.name : undefined
      });
      showToast({
        type: 'error',
        title: 'Error',
        message: `Failed to create unlock job: ${error instanceof Error ? error.message : String(error)}`
      });
    } finally {
      setCreatingUnlock(false);
    }
  }, [enrichmentStatus, makeRequest, showToast]);

  // Monitor unlock job completion using global job polling
  React.useEffect(() => {
    const handleUnlockJobCompletion = async () => {
      if (currentStep === 'unlocking' && unlockJobId && state.activeJobs) {
        const unlockJob = state.activeJobs.find(job => 
          job.job_id === unlockJobId && 
          job.job_type === 'contacts_unlock'
        );
        
        if (unlockJob) {
          if (unlockJob.status === 'completed') {
            setUnlockJobResult(unlockJob.result);
            showToast({
              type: 'success',
              title: 'Unlock Complete',
              message: 'Contacts have been added to your list. Ready to export.'
            });
            setCurrentStep('unlock_complete');
            
            // Try to manually update enrichment status if this is from an enrichment
            const enrichmentMetadata = unlockJob.params?.enrichment_metadata;
            if (enrichmentMetadata?.upload_id && enrichmentMetadata?.enrichment_id) {
              try {
                // Call the enrichment status update endpoint
                const response = await makeRequest(`${API_BASE_URL}/api/enrichment/source/csv/contacts/update-status`, {
                  method: 'POST',
                  body: JSON.stringify({
                    upload_id: enrichmentMetadata.upload_id,
                    enrichment_id: enrichmentMetadata.enrichment_id,
                    status: 'completed'
                  })
                });
                
                if (response?.data?.success) {
                } else {
                  console.warn('⚠️ Failed to update enrichment status:', response?.data?.message);
                }
              } catch (error) {
                console.warn('⚠️ Failed to update enrichment status:', error);
              }
            }
          } else if (unlockJob.status === 'failed') {
            console.error('❌ Unlock job failed:', unlockJob);
            showToast({
              type: 'error',
              title: 'Unlock Failed',
              message: unlockJob.error || 'Failed to unlock contacts'
            });
            setCurrentStep('matches_found'); // Go back to matches step
          }
        }
      }
    };

    handleUnlockJobCompletion();
  }, [currentStep, unlockJobId, state.activeJobs, showToast, makeRequest]);

  // Monitor export job completion using global job polling
  React.useEffect(() => {
    const handleExportJobCompletion = async () => {
      if (currentStep === 'exporting' && exportJobId && state.activeJobs) {
        const exportJob = state.activeJobs.find(job => 
          job.job_id === exportJobId && 
          job.job_type === 'enrichment_export'
        );
        
        if (exportJob) {
          if (exportJob.status === 'completed') {
            showToast({
              type: 'success',
              title: 'Export Complete',
              message: `Successfully exported ${exportJob.result?.record_count || 0} enriched records with source data`
            });
            setCurrentStep('complete');
            
            // Auto-download the file (same as list page export)
            if (exportJob.result?.signed_download_url) {
              window.open(exportJob.result.signed_download_url, '_blank');
            }
          } else if (exportJob.status === 'failed') {
            console.error('❌ Export job failed:', exportJob);
            showToast({
              type: 'error',
              title: 'Export Failed',
              message: exportJob.error || 'Failed to export enriched data'
            });
            setCurrentStep('unlock_complete'); // Go back to unlock complete step
          }
        }
      }
    };

    handleExportJobCompletion();
  }, [currentStep, exportJobId, state.activeJobs, showToast]);

  // Create export job using job system (same as list page export)
  const createExportJob = useCallback(async (passedTargetListId?: string, exportFields?: string[], format: 'csv' | 'json' = 'csv') => {
    const targetListId = passedTargetListId || enrichmentStatus?.target_list_id || uploadDetails?.target_list_id;
    
    if (!targetListId) {
      console.error('No target_list_id available for export job');
      showToast({
        type: 'error',
        title: 'Export Failed',
        message: 'No target list ID available for export'
      });
      return;
    }
    
    setCreatingExport(true);
    setCurrentStep('exporting');
    
    try {
      
      const response = await makeRequest<{
        job_id: string;
        status: string;
        message: string;
      }>(`${API_BASE_URL}/api/enrichment/destination/csv/contacts/export`, {
        method: 'POST',
        body: JSON.stringify({
          upload_id: uploadDetails?.upload_id,
          enrichment_id: enrichmentStatus?.enrichment_id,
          format,
          export_fields: exportFields
        })
      });
      

      if (response?.data?.job_id) {
        setExportJobId(response.data.job_id);
        showToast({
          type: 'success',
          title: 'Export Started',
          message: 'Enrichment export job created. Processing...'
        });
        
        // Add job to global polling system (same as list page export)
        addJobId(response.data.job_id, 'enrichment_export');
        
        setCurrentStep('exporting');
      } else {
        console.error('❌ Export job creation failed:', response);
        throw new Error(`Failed to create enrichment export job: ${response?.data?.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('❌ Failed to create enrichment export:', error);
      let errorMessage = 'Unknown error';
      let errorTitle = 'Export Failed';
      
      if (error instanceof Error) {
        errorMessage = error.message;
        // Handle specific unlock requirement error - check for various error messages
        if (errorMessage.includes('must be unlocked before export') || 
            errorMessage.includes('unlock process first') ||
            errorMessage.includes('Payment required')) {
          errorTitle = 'Unlock Required';
          errorMessage = 'Please complete the unlock process before exporting. Click "Unlock Contacts" first.';
          // Reset to unlock step if unlock is required
          setCurrentStep('unlock_complete');
          // Don't show duplicate toast - makeRequest already shows one for 402 errors
          return;
        }
        
        // Handle enrichment not completed error
        if (errorMessage.includes('Enrichment process not completed yet') ||
            errorMessage.includes('not completed yet')) {
          errorTitle = 'Enrichment Not Ready';
          errorMessage = 'The enrichment process is still running. Please wait a moment and try again.';
          setCurrentStep('unlock_complete');
          return;
        }
      } else if (typeof error === 'string') {
        errorMessage = error;
      }
      
      showToast({
        type: 'error',
        title: errorTitle,
        message: errorMessage
      });
    } finally {
      setCreatingExport(false);
    }
  }, [enrichmentStatus, uploadDetails, makeRequest, showToast]);


  // Start unlock process when user confirms
  const startUnlockProcess = useCallback(async () => {
    if (!enrichmentStatus?.person_uuids?.length) return;
    
    if (selectedListIds.size === 0) {
      showToast({
        type: 'error',
        title: 'No Lists Selected',
        message: 'Please select at least one contact list to add the contacts to'
      });
      return;
    }
    
    setCurrentStep('unlocking');
    try {
      await createUnlockJobs(enrichmentStatus.person_uuids, Array.from(selectedListIds), enrichmentStatus);
    } catch (error) {
      console.error('Error in startUnlockProcess:', error);
      showToast({
        type: 'error',
        title: 'Unlock Failed',
        message: 'Failed to start unlock process'
      });
    }
  }, [enrichmentStatus, selectedListIds, createUnlockJobs, showToast]);

  // Go to list selection step
  const proceedToListSelection = useCallback(() => {
    setCurrentStep('list_selection');
    loadContactLists();
  }, [loadContactLists]);

  // Handle escape key
  React.useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const renderStepContent = () => {
    if (currentStep === 'upload') {
      return (
        <div className="step-content">
          <div className="step-header">
            <h3>Upload CSV File</h3>
            <p>Select a CSV file containing contact information to enrich</p>
          </div>
          
          <div className="form-group">
            <label htmlFor="enrichment-name">Enrichment Name</label>
            <input
              id="enrichment-name"
              type="text"
              value={enrichmentName}
              onChange={(e) => setEnrichmentName(e.target.value)}
              placeholder="Enter a name for this enrichment"
              className="form-input"
            />
          </div>

          <div className="file-upload-area" onClick={() => fileInputRef.current?.click()}>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
            {selectedFile ? (
              <div className="file-selected">
                <FileText size={24} />
                <span>{selectedFile.name}</span>
                <span className="file-size">
                  {(selectedFile.size / 1024).toFixed(1)} KB
                </span>
              </div>
            ) : (
              <div className="file-upload-placeholder">
                <Upload size={32} />
                <span>Click to select CSV file</span>
                <span className="file-upload-hint">or drag and drop</span>
              </div>
            )}
          </div>

          <div className="step-actions">
            <button
              className="button-primary"
              onClick={handleUpload}
              disabled={!selectedFile || !enrichmentName || uploading}
            >
              {uploading ? 'Uploading...' : 'Upload CSV'}
            </button>
          </div>
        </div>
      );
    }

    if (currentStep === 'mapping') {
      return (
        <div className="step-content">
          <div className="step-header">
            <h3>Review Column Mapping</h3>
            <p>Review the detected column mapping for your CSV file</p>
          </div>

          {loadingMapping ? (
            <div className="loading-section">
              <div className="loading-spinner"></div>
              <p>Loading column mapping...</p>
            </div>
          ) : (
            <>
              <div className="mapping-details">
                <div className="file-info">
                  <h4>File: {uploadDetails?.file_name}</h4>
                  <p>Headers: {uploadDetails?.headers?.length || 0} columns detected</p>
                </div>

                {columnMapping && uploadDetails?.headers && (
                  <div className="column-mapping">
                    <h4>Column Mapping:</h4>
                    <p className="mapping-description">Select which columns from your CSV file should be used for each field:</p>
                    <div className="mapping-items">
                      <div className="mapping-item">
                        <label htmlFor="linkedin-mapping">
                          <strong>LinkedIn URL:</strong>
                        </label>
                        <select
                          id="linkedin-mapping"
                          value={userMapping.linkedin_url_column || ''}
                          onChange={(e) => handleMappingChange('linkedin_url_column', e.target.value)}
                          className="mapping-select"
                        >
                          <option value="">No mapping</option>
                          {uploadDetails.headers.map((header: string) => (
                            <option key={header} value={header}>
                              {header}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="mapping-item">
                        <label htmlFor="email-mapping">
                          <strong>Email:</strong>
                        </label>
                        <select
                          id="email-mapping"
                          value={userMapping.email_column || ''}
                          onChange={(e) => handleMappingChange('email_column', e.target.value)}
                          className="mapping-select"
                        >
                          <option value="">No mapping</option>
                          {uploadDetails.headers.map((header: string) => (
                            <option key={header} value={header}>
                              {header}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="mapping-item">
                        <label htmlFor="phone-mapping">
                          <strong>Phone:</strong>
                        </label>
                        <select
                          id="phone-mapping"
                          value={userMapping.phone_column || ''}
                          onChange={(e) => handleMappingChange('phone_column', e.target.value)}
                          className="mapping-select"
                        >
                          <option value="">No mapping</option>
                          {uploadDetails.headers.map((header: string) => (
                            <option key={header} value={header}>
                              {header}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button
                  className="button-secondary"
                  onClick={() => setCurrentStep('upload')}
                >
                  Back to Upload
                </button>
                <button
                  className="button-primary"
                  onClick={startEnrichment}
                  disabled={startingEnrichment}
                >
                  {startingEnrichment ? 'Searching...' : 'Search for Matches'}
                </button>
              </div>
            </>
          )}
        </div>
      );
    }

    if (currentStep === 'enriching') {
      return (
        <div className="step-content">
          <div className="step-header">
            <h3>Enriching Your Data</h3>
            <p>Searching for matching contacts in our database...</p>
          </div>

          <div className="loading-section">
            <div className="loading-spinner"></div>
            <p>Processing your file...</p>
            <p className="processing-note">
              This may take a few minutes depending on the size of your file.
            </p>
          </div>
        </div>
      );
    }

    if (currentStep === 'results') {
      const matchCount = searchResults?.person_uuids?.length || 0;
      const stats = searchResults?.stats || {};
      const totalRows = stats.total_csv_rows || 0;
      const matchRate = stats.match_rate_percent || 0;
      const linkedinMatches = stats.linkedin_matches || 0;
      const emailMatches = stats.email_matches || 0;
      const phoneMatches = stats.phone_matches || 0;
      const searchCriteria = stats.search_criteria || {};
      const fileInfo = stats.file_info || {};
      
      return (
        <div className="step-content results-step">
          <div className="step-header">
            <h3>Search Results</h3>
            <p>We found contacts in our database that match your CSV data.</p>
          </div>

          <div className="completion-summary">
            <div className="summary-item">
              <strong>Contacts Found:</strong>
              <span>{matchCount}</span>
            </div>
            <div className="summary-item">
              <strong>Match Rate:</strong>
              <span>{matchRate}%</span>
            </div>
            <div className="summary-item">
              <strong>CSV Rows Processed:</strong>
              <span>{totalRows}</span>
            </div>
          </div>

          {/* Match breakdown */}
          <div className="completion-summary">
            <div className="summary-item">
              <strong>LinkedIn Matches:</strong>
              <span>{linkedinMatches}</span>
            </div>
            <div className="summary-item">
              <strong>Email Matches:</strong>
              <span>{emailMatches}</span>
            </div>
            <div className="summary-item">
              <strong>Phone Matches:</strong>
              <span>{phoneMatches}</span>
            </div>
          </div>

          {/* Search criteria info */}
          {(searchCriteria.linkedin_urls_searched > 0 || searchCriteria.emails_searched > 0 || searchCriteria.phones_searched > 0) && (
            <div className="completion-summary">
              <div className="summary-item">
                <strong>Search Criteria:</strong>
                <span>
                  {[
                    searchCriteria.linkedin_urls_searched > 0 && `${searchCriteria.linkedin_urls_searched} LinkedIn URLs`,
                    searchCriteria.emails_searched > 0 && `${searchCriteria.emails_searched} emails`,
                    searchCriteria.phones_searched > 0 && `${searchCriteria.phones_searched} phone numbers`
                  ].filter(Boolean).join(', ')}
                </span>
              </div>
            </div>
          )}

          <div className="step-actions">
            <button
              className="button-primary"
              onClick={async () => {
                // Set the search results as enrichment status for the unlock process
                const enrichmentData = {
                  person_uuids: searchResults?.person_uuids || [],
                  target_list_id: uploadDetails?.target_list_id,
                  enrichment_id: searchResults?.enrichment_id,
                  upload_id: uploadId, // Include upload_id for enrichment metadata
                  stats: searchResults?.stats || {}
                };
                
                // Set enrichment status FIRST, then go to list selection
                setEnrichmentStatus(enrichmentData);
                proceedToListSelection();
              }}
              disabled={!searchResults?.person_uuids?.length}
            >
              Unlock {searchResults?.person_uuids?.length || 0} Matches
            </button>
          </div>
        </div>
      );
    }

    if (currentStep === 'matches_found') {
      const matchCount = enrichmentStatus?.person_uuids?.length || 0;
      const totalRows = enrichmentStatus?.stats?.total_csv_rows || enrichmentStatus?.stats?.total_rows || 0;
      const matchRate = enrichmentStatus?.stats?.match_rate_percent || enrichmentStatus?.stats?.match_rate || 0;
      
      return (
        <div className="step-content">
          <div className="step-header">
            <h3>Matches Found</h3>
            <p>We found contacts in our database that match your CSV data.</p>
          </div>

          <div className="completion-summary">
            <div className="summary-item">
              <strong>Contacts Found:</strong>
              <span>{matchCount}</span>
            </div>
            <div className="summary-item">
              <strong>Total Rows Processed:</strong>
              <span>{totalRows}</span>
            </div>
            <div className="summary-item">
              <strong>Match Rate:</strong>
              <span>{matchRate.toFixed(1)}%</span>
            </div>
            {enrichmentStatus?.estimated_cost && (
              <div className="summary-item">
                <strong>Estimated Cost:</strong>
                <span>${enrichmentStatus.estimated_cost.toFixed(2)}</span>
              </div>
            )}
          </div>


          <div className="step-actions">
            <button
              className="button-secondary"
              onClick={() => {
                onComplete();
                onClose();
              }}
            >
              Skip for Now
            </button>
            <button
              className="button-primary"
              onClick={proceedToListSelection}
              disabled={!matchCount}
            >
              Select Lists & Unlock Contacts
            </button>
          </div>
        </div>
      );
    }

    if (currentStep === 'list_selection') {
      const matchCount = enrichmentStatus?.person_uuids?.length || 0;
      
      return (
        <div className="step-content">
          <div className="modal-header">
            <h2>Unlock Contacts</h2>
            <button className="modal-close" onClick={() => setCurrentStep('matches_found')}>
              <X size={20} />
            </button>
          </div>

          <div className="modal-body">
            <div className="selection-summary">
              <span>Adding {matchCount} contact{matchCount !== 1 ? 's' : ''} to:</span>
            </div>

            {/* Create New List Section */}
            <div className="create-list-section">
              <button
                className={`create-list-toggle ${showCreateList ? 'active' : ''}`}
                onClick={() => setShowCreateList(!showCreateList)}
              >
                <FolderPlus size={16} />
                <span>Create New List</span>
              </button>
              
              {showCreateList && (
                <div className="create-list-form">
                  <div className="form-group">
                    <label htmlFor="list-name">List Name *</label>
                    <input
                      id="list-name"
                      type="text"
                      value={newListName}
                      onChange={(e) => setNewListName(e.target.value)}
                      placeholder="Enter list name..."
                      maxLength={200}
                    />
                  </div>
                  
                  <div className="form-actions">
                    <button
                      className="button-secondary"
                      onClick={() => {
                        setShowCreateList(false);
                        setNewListName('');
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      className="button-primary"
                      onClick={async () => {
                        if (!newListName.trim()) {
                          showToast({
                            type: 'error',
                            title: 'Invalid Name',
                            message: 'Please enter a list name'
                          });
                          return;
                        }
                        
                        try {
                          const response = await createList(newListName.trim(), `List created from contact enrichment: ${enrichmentName}`) as { list_id?: string };
                          if (response?.list_id) {
                            setSelectedListIds(prev => new Set([...prev, response.list_id!]));
                            setShowCreateList(false);
                            setNewListName('');
                            loadContactLists(); // Refresh the list
                            showToast({
                              type: 'success',
                              title: 'List Created',
                              message: `Created list "${newListName.trim()}" and selected it`
                            });
                          } else {
                            throw new Error('Failed to create list');
                          }
                        } catch (error) {
                          console.error('Failed to create list:', error);
                          showToast({
                            type: 'error',
                            title: 'Create Failed',
                            message: 'Failed to create new list'
                          });
                        }
                      }}
                      disabled={!newListName.trim()}
                    >
                      Create
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Existing Lists Section */}
            <div className="existing-lists-section">
              <h3>Existing Contact Lists</h3>
              
              {loadingLists ? (
                <div className="loading-state">
                  <div className="loading-spinner"></div>
                  <p>Loading contact lists...</p>
                </div>
              ) : contactLists.length === 0 ? (
                <div className="empty-state">
                  <p>No contact lists found. Create one above.</p>
                </div>
              ) : (
                <div className="lists-grid">
                  {contactLists.map((list) => (
                    <div
                      key={list.list_id}
                      className={`list-item ${selectedListIds.has(list.list_id) ? 'selected' : ''}`}
                      onClick={() => {
                        setSelectedListIds(prev => {
                          const newSet = new Set(prev);
                          if (newSet.has(list.list_id)) {
                            newSet.delete(list.list_id);
                          } else {
                            newSet.add(list.list_id);
                          }
                          return newSet;
                        });
                      }}
                    >
                      <div className="list-item-header">
                        <div className="list-item-checkbox">
                          <input
                            type="checkbox"
                            checked={selectedListIds.has(list.list_id)}
                            onChange={() => {
                              setSelectedListIds(prev => {
                                const newSet = new Set(prev);
                                if (newSet.has(list.list_id)) {
                                  newSet.delete(list.list_id);
                                } else {
                                  newSet.add(list.list_id);
                                }
                                return newSet;
                              });
                            }}
                          />
                          <div className="checkbox-visual">
                            {selectedListIds.has(list.list_id) && <div className="checkmark">✓</div>}
                          </div>
                        </div>
                        <div className="list-item-info">
                          <h4 className="list-name">{list.name}</h4>
                          <div className="list-meta">
                            <span className="contact-count">
                              {list.contact_count || 0} contact{(list.contact_count || 0) !== 1 ? 's' : ''}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="modal-footer">
            <div className="selection-info">
              {selectedListIds.size > 0 && (
                <span>{selectedListIds.size} list{selectedListIds.size !== 1 ? 's' : ''} selected</span>
              )}
            </div>
            <div className="modal-actions">
              <button
                className="button-secondary"
                onClick={() => setCurrentStep('matches_found')}
              >
                Back
              </button>
              <button
                className="button-primary"
                onClick={startUnlockProcess}
                disabled={selectedListIds.size === 0}
              >
                Unlock {matchCount} Contact{matchCount !== 1 ? 's' : ''}
              </button>
            </div>
          </div>
        </div>
      );
    }

    if (currentStep === 'unlocking') {
      return (
        <div className="step-content">
          <div className="step-header">
            <h3>Unlocking Contacts</h3>
            <p>Adding found contacts to your list...</p>
          </div>

          <div className="loading-section">
            <div className="loading-spinner"></div>
            <p>Unlocking contacts...</p>
            <p className="processing-note">
              Found {enrichmentStatus?.person_uuids?.length || 0} contacts to unlock.
            </p>
          </div>
        </div>
      );
    }

    if (currentStep === 'unlock_complete') {
      const contactCount = enrichmentStatus?.person_uuids?.length || 0;
      const listName = enrichmentName || 'your list';
      
      // Debug unlock job result
      
      // Try multiple cost field paths
      const unlockCost = unlockJobResult?.billing?.job_cost || 
                        unlockJobResult?.billing?.amount_spent || 
                        unlockJobResult?.total_cost ||
                        unlockJobResult?.cost;
      
      return (
        <div className="step-content">
          <div className="step-header">
            <h3>Contacts Unlocked Successfully!</h3>
            <p>Your enriched contacts have been added to <strong>"{listName}"</strong> and are ready to export.</p>
          </div>

          <div className="completion-summary">
            <div className="summary-item">
              <strong>Contacts Added to List:</strong>
              <span>{contactCount}</span>
            </div>
            <div className="summary-item">
              <strong>List Name:</strong>
              <span>"{listName}"</span>
            </div>
            <div className="summary-item">
              <strong>Unlock Cost:</strong>
              <span>
                {unlockCost ? 
                  `$${parseFloat(String(unlockCost)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : 
                  'No cost data available'
                }
              </span>
            </div>
            <div className="summary-item">
              <strong>Status:</strong>
              <span>✅ Ready to export to CSV</span>
            </div>
          </div>

          <div className="step-actions">
            <button
              className="button-primary"
              onClick={() => {
                setCurrentStep('exporting');
              }}
              disabled={!contactCount}
            >
              Select Fields & Export
            </button>
          </div>
        </div>
      );
    }

    if (currentStep === 'exporting') {
      const filteredGroups = fieldGroups.map(group => ({
        ...group,
        fields: group.fields.filter((field: any) => 
          field.definition.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
          field.name.toLowerCase().includes(searchTerm.toLowerCase())
        )
      })).filter(group => group.fields.length > 0);

      const handleExport = async () => {
        const targetListId = enrichmentStatus?.target_list_id || uploadDetails?.target_list_id;
        if (!targetListId) {
          showToast({
            type: 'error',
            title: 'Export Failed',
            message: 'No target list ID available for export'
          });
          return;
        }
        
        try {
          await createExportJob(targetListId, Array.from(selectedFields), format);
        } catch (error) {
          console.error('Failed to start export job:', error);
          showToast({
            type: 'error',
            title: 'Export Failed',
            message: 'Failed to start export job'
          });
        }
      };

      return (
        <div className="step-content">
          <div className="step-header">
            <h3>Select Export Fields</h3>
            <p>Choose which fields to include in your export file.</p>
          </div>

          <div className="export-fields-content">
            {loadingFields ? (
              <div style={{ textAlign: 'center', padding: '30px' }}>
                <div className="spinner" />
                <p>Loading fields...</p>
              </div>
            ) : (
              <>
                {/* Format & Actions in one row */}
                <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', gap: '12px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px' }}>
                      <input
                        type="radio"
                        value="csv"
                        checked={format === 'csv'}
                        onChange={(e) => setFormat(e.target.value as 'csv' | 'json')}
                      />
                      CSV
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px' }}>
                      <input
                        type="radio"
                        value="json"
                        checked={format === 'json'}
                        onChange={(e) => setFormat(e.target.value as 'csv' | 'json')}
                      />
                      JSON
                    </label>
                  </div>
                  
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      onClick={selectAllFields}
                      className="button-secondary"
                      style={{ fontSize: '12px', padding: '4px 8px' }}
                    >
                      Select All
                    </button>
                    <button
                      type="button"
                      onClick={selectDefaultFields}
                      className="button-secondary"
                      style={{ fontSize: '12px', padding: '4px 8px' }}
                    >
                      Default
                    </button>
                    <button
                      type="button"
                      onClick={clearAllFields}
                      className="button-secondary"
                      style={{ fontSize: '12px', padding: '4px 8px' }}
                    >
                      Clear All
                    </button>
                  </div>
                </div>

                {/* Search */}
                <div style={{ marginBottom: '16px' }}>
                  <input
                    type="text"
                    placeholder="Search fields..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  />
                </div>

                {/* Field Groups */}
                <div className="field-groups">
                  {filteredGroups.map((group, groupIndex) => (
                    <div key={groupIndex} className="field-group" style={{ marginBottom: '20px' }}>
                      <h4 style={{ 
                        margin: '0 0 8px 0', 
                        fontSize: '14px', 
                        fontWeight: '600', 
                        color: '#374151',
                        borderBottom: '1px solid #e5e7eb',
                        paddingBottom: '4px'
                      }}>
                        {group.name}
                      </h4>
                      <div className="field-list" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {group.fields.map((field: any, fieldIndex: number) => (
                          <label
                            key={fieldIndex}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '12px',
                              padding: '12px',
                              border: '1px solid #e5e7eb',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '14px',
                              backgroundColor: selectedFields.has(field.name) ? '#f0f9ff' : 'white',
                              transition: 'background-color 0.2s ease'
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={selectedFields.has(field.name)}
                              onChange={() => handleFieldToggle(field.name)}
                              style={{ margin: 0, width: '16px', height: '16px' }}
                            />
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: '500', marginBottom: '2px' }}>{field.definition.label}</div>
                              <div style={{ color: '#6b7280', fontSize: '12px' }}>{field.name}</div>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          <div className="step-actions">
            <div className="selection-info" style={{ 
              fontSize: '14px', 
              color: '#6b7280', 
              fontWeight: '500',
              marginRight: 'auto'
            }}>
              <span>{selectedFields.size} field{selectedFields.size !== 1 ? 's' : ''} selected</span>
            </div>
            <button 
              className="button-secondary" 
              onClick={() => setCurrentStep('unlock_complete')}
            >
              Back
            </button>
            <button
              className="button-primary"
              onClick={handleExport}
              disabled={selectedFields.size === 0 || loadingFields}
            >
              Export {selectedFields.size} Field{selectedFields.size !== 1 ? 's' : ''}
            </button>
          </div>
        </div>
      );
    }

    if (currentStep === 'export_complete') {
      const contactCount = finalResults?.contactCount || searchResults?.person_uuids?.length || enrichmentStatus?.person_uuids?.length || 0;
      
      return (
        <div className="step-content">
          <div className="step-header">
            <h3>Export Complete!</h3>
            <p>Your enriched contact data has been exported and is ready for download.</p>
          </div>

          <div className="completion-summary">
            <div className="summary-item">
              <strong>Contacts Exported:</strong>
              <span>{contactCount}</span>
            </div>
            <div className="summary-item">
              <strong>Status:</strong>
              <span>✅ Export file created successfully</span>
            </div>
          </div>

          <div className="step-actions">
            <button
              className="button-primary"
              onClick={() => {
                onComplete();
                onClose();
              }}
            >
              Close
            </button>
          </div>
        </div>
      );
    }

    if (currentStep === 'complete') {
      // Use finalResults if available (from export completion), otherwise fall back to search results
      const contactCount = finalResults?.contactCount || searchResults?.person_uuids?.length || enrichmentStatus?.person_uuids?.length || 0;
      
      return (
        <div className="step-content">
          <div className="step-header">
            <h3>Process Complete!</h3>
            <p>Your CSV enrichment process has been completed successfully.</p>
          </div>

          <div className="completion-summary">
            <div className="summary-item">
              <strong>Contacts Found:</strong>
              <span>{contactCount}</span>
            </div>
            <div className="summary-item">
              <strong>Status:</strong>
              <span>✅ Process completed successfully</span>
            </div>
          </div>

          <div className="step-actions">
            <button
              className="button-primary"
              onClick={() => {
                onComplete();
                onClose();
              }}
            >
              Close
            </button>
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className={`modal-content ${currentStep === 'list_selection' ? 'list-selection-modal' : 'new-enrichment-modal'} ${currentStep === 'results' ? 'results-modal' : ''}`} onClick={e => e.stopPropagation()}>
        {currentStep !== 'list_selection' && (
          <div className="modal-header">
            <h2>New CSV Enrichment</h2>
            <button className="modal-close" onClick={onClose}>
              <X size={20} />
            </button>
          </div>
        )}

        <div className="modal-body">
          {/* Step Content */}
          {renderStepContent()}
        </div>
      </div>
    </div>
  );
};

export default NewEnrichmentModal;

